import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { getShiftStatusMeta } from '@/lib/shiftStatusMeta';
import { ServiceActivityBreakdownPanel } from '@/components/calendar/ServiceActivityBreakdownPanel';
import { getCalendarSummaryMetrics, extractFilterOptions, applyClientFilters } from '@/lib/calendarMetrics';
import { buildMonthCells, groupShiftsByDate, getDayStatusSummary } from '@/lib/monthGridHelper';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SavedViewsMenu } from '@/components/roster/SavedViewsMenu';
import { sanitizeState, getDefaultView } from '@/lib/savedCalendarViews';
import { CalendarOperationalCharts } from './CalendarOperationalCharts';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Users, User, Home,
  Clock, AlertTriangle, CheckCircle, Plus, Filter, RefreshCw, Grid3X3, List,
  GripVertical, Move, CalendarDays, XCircle, BarChart3, Timer, Trash2, UserPlus,
  Keyboard, PieChart as PieIcon, Play, Square, LogIn, LogOut,
} from 'lucide-react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addMonths, subMonths, isSameDay, parseISO, addWeeks, subWeeks } from 'date-fns';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { ShiftPeriodBadgeForShift } from '@/components/calendar/ShiftPeriodBadge';

// ════════════════════════════════════════════════════════════════
// VIEW MODES & GROUP OPTIONS (unchanged from Day 1 baseline)
// ════════════════════════════════════════════════════════════════

const VIEW_MODES = [
  { value: 'week', label: 'Week', icon: Grid3X3, disabled: false },
  { value: 'day', label: 'Day', icon: List, disabled: false },
  { value: 'month', label: 'Month', icon: CalendarDays, disabled: false },
];

const GROUP_BY_OPTIONS = [
  { value: 'none', label: 'All Shifts' },
  { value: 'staff', label: 'By Staff' },
  { value: 'participant', label: 'By Participant' },
  { value: 'house', label: 'By House' },
  { value: 'team', label: 'By Team' },
];

// ════════════════════════════════════════════════════════════════
// StatusBadge — Pure presentational, uses getShiftStatusMeta
// ════════════════════════════════════════════════════════════════

function StatusBadge({ status, className = '' }) {
  const meta = getShiftStatusMeta(status);
  return (
    <Badge variant="outline" className={`${meta.bgClass} border text-xs ${className}`} data-testid={`status-badge-${status || 'unknown'}`}>
      {meta.label}
    </Badge>
  );
}

// ════════════════════════════════════════════════════════════════
// CalendarToolbar — Improved layout, same behaviour
// ════════════════════════════════════════════════════════════════

function CalendarToolbar({
  currentDate, weekStart, weekEnd, viewMode, setViewMode,
  groupBy, setGroupBy, statusFilter, setStatusFilter,
  staffFilter, setStaffFilter, participantFilter, setParticipantFilter,
  serviceFilter, setServiceFilter,
  filterOptions, activeFilterCount, onClearFilters,
  onPrev, onNext, onToday, onRefresh, onCreateShift, onOpenKeyboardHelp,
  savedViewsSlot,
}) {
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="space-y-3" data-testid="calendar-toolbar">
      {/* Top bar: Title + actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-blue-500" />
            Roster Calendar
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Visual shift management and staff assignment
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onOpenKeyboardHelp} data-testid="keyboard-help-btn" title="Keyboard shortcuts (?)" className="px-2">
            <Keyboard className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
          <Button size="sm" onClick={onCreateShift} data-testid="create-shift-btn">
            <Plus className="w-4 h-4 mr-1" />Create Shift
          </Button>
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex items-center justify-between bg-muted/30 rounded-lg px-3 py-2.5">
        {/* Left: Date navigation */}
        <div className="flex items-center gap-2">
          <div className="flex border rounded-md">
            <Button variant="ghost" size="sm" onClick={onPrev} className="rounded-r-none px-2" data-testid="nav-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onToday} className="rounded-none border-x px-3 text-xs" data-testid="nav-today">
              Today
            </Button>
            <Button variant="ghost" size="sm" onClick={onNext} className="rounded-l-none px-2" data-testid="nav-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <span className="font-semibold text-sm ml-1" data-testid="date-range-label">
            {viewMode === 'day'
              ? format(currentDate, 'EEEE, MMMM d, yyyy')
              : viewMode === 'month'
              ? format(currentDate, 'MMMM yyyy')
              : `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`}
          </span>
        </div>

        {/* Right: Filters + View mode */}
        <div className="flex items-center gap-2">
          {/* Filter toggle */}
          <Button
            variant={showFilters || activeFilterCount > 0 ? 'default' : 'outline'}
            size="sm"
            className="h-8 text-xs"
            onClick={() => setShowFilters(v => !v)}
            data-testid="filter-toggle-btn"
          >
            <Filter className="w-3.5 h-3.5 mr-1" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{activeFilterCount}</Badge>
            )}
          </Button>

          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground" onClick={onClearFilters} data-testid="clear-filters-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" />Clear
            </Button>
          )}

          {savedViewsSlot}

          {/* Group By */}
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="group-by-select">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              {GROUP_BY_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6" />

          {/* View mode toggle */}
          <div className="flex border rounded-md" data-testid="view-mode-toggle">
            {VIEW_MODES.map(mode => (
              <Button
                key={mode.value}
                variant={viewMode === mode.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => !mode.disabled && setViewMode(mode.value)}
                disabled={mode.disabled}
                className="rounded-none first:rounded-l-md last:rounded-r-md px-2.5 h-8"
                title={mode.disabled ? `${mode.label} view (coming soon)` : `${mode.label} view`}
                data-testid={`view-${mode.value}`}
              >
                <mode.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Filter panel (collapsible) */}
      {showFilters && (
        <div className="bg-muted/20 border rounded-lg p-3 space-y-3 animate-fade-in" data-testid="filter-panel">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground">Filter Shifts</p>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" className="h-6 text-xs text-muted-foreground" onClick={onClearFilters}>
                Clear all filters
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Status */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-status">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open / Partial</SelectItem>
                  <SelectItem value="OPEN">Open Only</SelectItem>
                  <SelectItem value="PARTIALLY_FILLED">Partial Only</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="NO_SHOW">No Show</SelectItem>
                  <SelectItem value="INVOICED">Invoiced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Staff */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Staff</label>
              <Select value={staffFilter || '_all'} onValueChange={v => setStaffFilter(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-staff">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Staff</SelectItem>
                  {filterOptions.staffOptions.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Participant */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Participant</label>
              <Select value={participantFilter || '_all'} onValueChange={v => setParticipantFilter(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-participant">
                  <SelectValue placeholder="All Participants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Participants</SelectItem>
                  {filterOptions.participantOptions.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Service */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Service</label>
              <Select value={serviceFilter || '_all'} onValueChange={v => setServiceFilter(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-service">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Services</SelectItem>
                  {filterOptions.serviceOptions.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ShiftCard — Uses StatusBadge, drag handle, unchanged logic
// ════════════════════════════════════════════════════════════════

function ShiftCard({ shift, compact = false, onClick, isDragging = false, dragHandleProps = null, selectable = false, selected = false, onToggleSelect }) {
  const isGS = shift._isGroupSession;
  const meta = isGS
    ? { bgClass: 'bg-indigo-100 border-indigo-300 text-indigo-800' }
    : getShiftStatusMeta(shift.status);
  const statusMeta = getShiftStatusMeta(shift.status);

  // Tooltip data sourced strictly from already-loaded shift object (no API calls)
  const assignedNames = shift.assigned_staff?.length > 0
    ? shift.assigned_staff.map(s => s.name).join(', ')
    : 'Unassigned';

  const body = (
    <div
      className={`${meta.bgClass} border rounded-md p-2 cursor-pointer hover:shadow-md transition-all duration-150 will-change-transform ${compact ? 'text-xs' : 'text-sm'} ${isGS ? 'border-dashed ring-1 ring-indigo-200/60' : ''} ${isDragging ? 'shadow-2xl ring-2 ring-primary/80 opacity-95 rotate-1 scale-[1.02] bg-opacity-95' : ''} ${selected ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
      onClick={() => onClick?.(shift)}
      data-testid={`shift-card-${shift.id}`}
      data-draggable={!isGS ? 'true' : 'false'}
      data-dragging={isDragging ? 'true' : 'false'}
    >
      <div className="flex items-start gap-1">
        {selectable && !isGS && (
          <div className="pt-0.5" onClick={e => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect?.(shift.id)}
              className="h-3.5 w-3.5"
              data-testid={`select-shift-${shift.id}`}
            />
          </div>
        )}
        {dragHandleProps && !isGS && (
          <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing pt-0.5" onClick={e => e.stopPropagation()} title="Drag to reschedule">
            <GripVertical className="w-3 h-3 text-current opacity-50 hover:opacity-100" />
          </div>
        )}
        {isGS && (
          <div className="pt-0.5 opacity-60 cursor-not-allowed" title="Group sessions cannot be dragged">
            <GripVertical className="w-3 h-3 text-current line-through" style={{ textDecoration: 'line-through' }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate flex items-center gap-1">
            {isGS && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-indigo-50 border-indigo-300 text-indigo-700 shrink-0">Group</Badge>}
            {shift.title || 'Shift'}
          </div>
          <div className="flex items-center gap-1 text-xs opacity-80 tabular-nums">
            <Clock className="w-3 h-3" />
            {shift.start_time}-{shift.end_time}
            <ShiftPeriodBadgeForShift shift={shift} className="ml-1" />
          </div>
        </div>
      </div>
      {!compact && (
        <>
          {shift.participant_name && (
            <div className="flex items-center gap-1 text-xs mt-1">
              <User className="w-3 h-3" />{shift.participant_name}
            </div>
          )}
          {shift.house_name && (
            <div className="flex items-center gap-1 text-xs mt-1">
              <Home className="w-3 h-3" />{shift.house_name}
            </div>
          )}
          {shift.assigned_staff?.length > 0 && (
            <div className="flex items-center gap-1 text-xs mt-1">
              <Users className="w-3 h-3" />{shift.assigned_staff.map(s => s.name).join(', ')}
            </div>
          )}
          {shift.required_staff > 1 && (
            <Badge variant="outline" className="text-xs mt-1">
              {shift.assigned_staff?.length || 0}/{shift.required_staff} staff
            </Badge>
          )}
        </>
      )}
    </div>
  );

  // Skip tooltip while dragging to avoid visual glitches
  if (isDragging) return body;

  return (
    <Tooltip delayDuration={350}>
      <TooltipTrigger asChild>{body}</TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-xs" data-testid={`shift-tooltip-${shift.id}`}>
        <div className="space-y-1 text-xs">
          <div className="font-semibold flex items-center gap-1.5 text-sm">
            {isGS && <span className="px-1.5 py-0.5 rounded bg-indigo-500/30 text-[9px] font-bold tracking-wide">GROUP</span>}
            {shift.title || 'Shift'}
          </div>
          <div className="flex items-center gap-1.5 opacity-90 tabular-nums">
            <Clock className="w-3 h-3" />
            {shift.date} • {shift.start_time}–{shift.end_time}
          </div>
          {shift.participant_name && (
            <div><span className="opacity-70">Participant:</span> <span className="font-medium">{shift.participant_name}</span></div>
          )}
          {shift.service_name && (
            <div><span className="opacity-70">Service:</span> <span className="font-medium">{shift.service_name}</span></div>
          )}
          {shift.house_name && (
            <div><span className="opacity-70">House:</span> <span className="font-medium">{shift.house_name}</span></div>
          )}
          {shift.team_name && (
            <div><span className="opacity-70">Team:</span> <span className="font-medium">{shift.team_name}</span></div>
          )}
          <div><span className="opacity-70">Staff:</span> <span className="font-medium">{assignedNames}</span></div>
          {shift.required_staff > 1 && (
            <div><span className="opacity-70">Coverage:</span> <span className="font-medium">{shift.assigned_staff?.length || 0}/{shift.required_staff}</span></div>
          )}
          {!isGS && (
            <div><span className="opacity-70">Status:</span> <span className="font-medium">{statusMeta.label}</span></div>
          )}
          {isGS && (
            <div className="pt-1 opacity-75 italic border-t border-white/10">Group session — managed via Group Sessions page</div>
          )}
          {!isGS && (
            <div className="pt-0.5 opacity-60 text-[10px]">Drag handle to reschedule • Click to open</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

// ════════════════════════════════════════════════════════════════
// DayColumn — Droppable zone, unchanged logic
// ════════════════════════════════════════════════════════════════

function DayColumn({ date, shifts, onShiftClick, onSlotClick, isToday, droppableId, selectedShiftIds, onToggleSelect }) {
  const dayShifts = shifts.filter(s => s.date === format(date, 'yyyy-MM-dd'));
  const sortedShifts = [...dayShifts].sort((a, b) => a.start_time.localeCompare(b.start_time));

  const handleSlotClick = (e) => {
    if (e.target === e.currentTarget || e.target.closest('[data-slot-click]')) {
      onSlotClick?.(format(date, 'yyyy-MM-dd'));
    }
  };

  return (
    <div className={`flex-1 min-w-[140px] border-r last:border-r-0 ${isToday ? 'bg-blue-50/50' : ''}`}>
      <div className={`text-center py-2 border-b font-medium ${isToday ? 'bg-blue-100 text-blue-700' : 'bg-muted/50'}`}>
        <div className="text-xs text-muted-foreground">{format(date, 'EEE')}</div>
        <div className={`text-lg ${isToday ? 'font-bold' : ''}`}>{format(date, 'd')}</div>
        {dayShifts.length > 0 && (
          <div className="text-xs text-muted-foreground">{dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''}</div>
        )}
      </div>
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`p-1 space-y-1 min-h-[200px] max-h-[500px] overflow-y-auto transition-all duration-150 ${snapshot.isDraggingOver ? 'bg-primary/10 ring-2 ring-primary/50 ring-inset' : ''}`}
            style={{
              backgroundImage: snapshot.isDraggingOver
                ? 'repeating-linear-gradient(to bottom, transparent 0px, transparent 39px, rgba(59, 130, 246, 0.22) 39px, rgba(59, 130, 246, 0.22) 40px)'
                : 'repeating-linear-gradient(to bottom, transparent 0px, transparent 39px, rgba(100, 116, 139, 0.10) 39px, rgba(100, 116, 139, 0.10) 40px)',
              backgroundSize: '100% 40px',
            }}
            data-droppable-id={droppableId}
            data-dragging-over={snapshot.isDraggingOver ? 'true' : 'false'}
          >
            {sortedShifts.length === 0 && !snapshot.isDraggingOver ? (
              <div data-slot-click className="text-xs text-muted-foreground text-center py-4 cursor-pointer hover:bg-muted/50 rounded h-full flex flex-col items-center justify-center min-h-[180px]" onClick={handleSlotClick}>
                <Plus className="w-4 h-4 mb-1 opacity-40" />
                <span>No shifts</span>
                <span className="text-[10px] opacity-60">Click to create</span>
              </div>
            ) : (
              <>
              {sortedShifts.map((shift, index) => (
                shift._isGroupSession ? (
                  <ShiftCard key={shift.id} shift={shift} compact onClick={onShiftClick} />
                ) : (
                  <Draggable key={shift.id} draggableId={shift.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                        <ShiftCard
                          shift={shift}
                          compact
                          onClick={onShiftClick}
                          isDragging={dragSnapshot.isDragging}
                          dragHandleProps={dragProvided.dragHandleProps}
                          selectable
                          selected={selectedShiftIds?.has(shift.id)}
                          onToggleSelect={onToggleSelect}
                        />
                      </div>
                    )}
                  </Draggable>
                )
              ))}
              <div data-slot-click className="flex items-center justify-center py-1.5 cursor-pointer hover:bg-muted/50 rounded mt-1 border border-dashed border-slate-300 opacity-50 hover:opacity-100 transition-opacity" onClick={handleSlotClick}>
                <Plus className="w-3.5 h-3.5 text-slate-400" />
              </div>
              </>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// GroupedView — unchanged logic
// ════════════════════════════════════════════════════════════════

function GroupedView({ shifts, groupBy, onShiftClick }) {
  const grouped = useMemo(() => {
    if (groupBy === 'none') return { 'All Shifts': shifts };
    const groups = {};
    shifts.forEach(shift => {
      // Group sessions always go under "Group Sessions" header
      if (shift._isGroupSession) {
        const key = 'Group Sessions';
        if (!groups[key]) groups[key] = [];
        groups[key].push(shift);
        return;
      }
      let key = 'Unassigned';
      if (groupBy === 'staff') {
        if (shift.assigned_staff?.length > 0) {
          shift.assigned_staff.forEach(s => {
            if (!groups[s.name]) groups[s.name] = [];
            groups[s.name].push(shift);
          });
          return;
        } else { key = 'Unassigned'; }
      } else if (groupBy === 'participant') {
        key = shift.participant_name || 'No Participant';
      } else if (groupBy === 'house') {
        key = shift.house_name || 'No House';
      } else if (groupBy === 'team') {
        key = shift.team_name || 'No Team';
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(shift);
    });
    return groups;
  }, [shifts, groupBy]);

  const sortedGroups = Object.entries(grouped).sort(([a], [b]) => {
    if (a === 'Unassigned' || a.startsWith('No ')) return 1;
    if (b === 'Unassigned' || b.startsWith('No ')) return -1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-4">
      {sortedGroups.map(([groupName, groupShifts]) => (
        <Card key={groupName}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2">
                {groupBy === 'staff' && <User className="w-4 h-4" />}
                {groupBy === 'participant' && <User className="w-4 h-4" />}
                {groupBy === 'house' && <Home className="w-4 h-4" />}
                {groupBy === 'team' && <Users className="w-4 h-4" />}
                {groupName}
              </h3>
              <Badge variant="outline">{groupShifts.length} shifts</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              {groupShifts.sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`)).map(shift => (
                <div key={shift.id} className="relative">
                  <div className="text-xs text-muted-foreground mb-1">{shift.date}</div>
                  <ShiftCard shift={shift} onClick={onShiftClick} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ShiftCheckInOut — Admin clock in/out controls
// ════════════════════════════════════════════════════════════════

function ShiftCheckInOut({ shift, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const isCheckedIn = !!shift.check_in_time && !shift.check_out_time;
  const isCompleted = !!shift.check_out_time;
  const canCheckIn = !shift.check_in_time && !isCompleted && 
    ['ASSIGNED', 'CONFIRMED', 'PARTIALLY_FILLED'].includes(shift.status);

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      await api.post(`/roster/shifts/${shift.id}/check-in`, {});
      toast.success('Checked in successfully');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to check in');
    } finally { setLoading(false); }
  };

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      await api.post(`/roster/shifts/${shift.id}/check-out`, {});
      toast.success('Checked out successfully');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed to check out');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-2">
      <Separator />
      <Label className="text-muted-foreground block">Attendance</Label>
      {shift.check_in_time && (
        <div className="text-xs space-y-1 bg-muted/50 rounded-md p-2">
          <div className="flex items-center gap-2">
            <LogIn className="w-3 h-3 text-green-600" />
            <span>Checked in: {new Date(shift.check_in_time).toLocaleString()}</span>
          </div>
          {shift.check_out_time && (
            <div className="flex items-center gap-2">
              <LogOut className="w-3 h-3 text-red-600" />
              <span>Checked out: {new Date(shift.check_out_time).toLocaleString()}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2">
        {canCheckIn && (
          <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleCheckIn} disabled={loading} data-testid="shift-check-in-btn">
            <Play className="h-4 w-4 mr-1" /> Check In
          </Button>
        )}
        {isCheckedIn && (
          <Button size="sm" variant="destructive" className="flex-1" onClick={handleCheckOut} disabled={loading} data-testid="shift-check-out-btn">
            <Square className="h-4 w-4 mr-1" /> Check Out
          </Button>
        )}
        {isCompleted && (
          <Badge variant="outline" className="flex-1 justify-center py-2">
            <CheckCircle className="h-4 w-4 mr-1" /> Completed
          </Badge>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// ShiftDetailDialog — unchanged logic, uses StatusBadge
// ════════════════════════════════════════════════════════════════

function ShiftDetailDialog({ shift, open, onClose, onUpdate, staff }) {
  const [assigning, setAssigning] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [conflicts, setConflicts] = useState(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  if (!shift) return null;

  const checkConflicts = async (staffId) => {
    if (!staffId) return;
    setCheckingConflicts(true);
    setConflicts(null);
    try {
      const res = await api.get(`/roster/staff/${staffId}/availability-conflicts`, {
        params: { shift_date: shift.date, start_time: shift.start_time, end_time: shift.end_time }
      });
      setConflicts(res.data.data);
    } catch (e) {
      console.error('Conflict check failed', e);
    } finally { setCheckingConflicts(false); }
  };

  const handleStaffSelect = (staffId) => {
    setSelectedStaff(staffId);
    setConflicts(null);
    if (staffId) checkConflicts(staffId);
  };

  const handleAssign = async () => {
    if (!selectedStaff) { toast.error('Please select a staff member'); return; }
    if (conflicts?.has_conflicts) {
      const highSeverity = conflicts.conflicts.filter(c => c.severity === 'HIGH');
      if (highSeverity.length > 0) {
        toast.error('Cannot assign: Staff has HIGH severity conflicts');
        return;
      }
    }
    setAssigning(true);
    try {
      await api.post(`/roster/shifts/${shift.id}/add-staff`, { staff_id: selectedStaff });
      toast.success('Staff assigned');
      onUpdate();
      setSelectedStaff('');
      setConflicts(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign staff');
    } finally { setAssigning(false); }
  };

  const handleRemoveStaff = async (staffId) => {
    try {
      await api.post(`/roster/shifts/${shift.id}/remove-staff`, { staff_id: staffId });
      toast.success('Staff removed');
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove staff');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" />
            {shift.title || 'Shift Details'}
          </DialogTitle>
          <DialogDescription>{shift.date} {shift.start_time}-{shift.end_time}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <StatusBadge status={shift.status} />
            {shift.required_staff > 1 && (
              <Badge variant="outline">{shift.assigned_staff?.length || 0}/{shift.required_staff} staff</Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            {shift.participant_name && (
              <div><span className="text-muted-foreground">Participant:</span><p className="font-medium">{shift.participant_name}</p></div>
            )}
            {shift.house_name && (
              <div><span className="text-muted-foreground">House:</span><p className="font-medium">{shift.house_name}</p></div>
            )}
            {shift.team_name && (
              <div><span className="text-muted-foreground">Team:</span><p className="font-medium">{shift.team_name}</p></div>
            )}
            {shift.service_name && (
              <div><span className="text-muted-foreground">Service:</span><p className="font-medium">{shift.service_name}</p></div>
            )}
          </div>

          <div>
            <Label className="mb-2 block">Assigned Staff</Label>
            {shift.assigned_staff?.length > 0 ? (
              <div className="space-y-2">
                {shift.assigned_staff.map(s => (
                  <div key={s.id} className="flex items-center justify-between bg-muted/50 rounded-md p-2">
                    <span className="text-sm font-medium">{s.name}</span>
                    <Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => handleRemoveStaff(s.id)}>Remove</Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No staff assigned</p>
            )}
          </div>

          {(shift.assigned_staff?.length || 0) < (shift.required_staff || 1) && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Select value={selectedStaff} onValueChange={handleStaffSelect}>
                  <SelectTrigger className="flex-1" data-testid="assign-staff-select">
                    <SelectValue placeholder="Select staff to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.filter(s => !shift.assigned_staff?.find(a => a.id === s.id)).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAssign} disabled={assigning || !selectedStaff || (conflicts?.has_conflicts && conflicts.conflicts.some(c => c.severity === 'HIGH'))}>
                  {assigning ? 'Assigning...' : 'Assign'}
                </Button>
              </div>

              {checkingConflicts && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />Checking availability...
                </div>
              )}
              {conflicts && !checkingConflicts && (
                <div className={`p-3 rounded-lg text-sm ${conflicts.has_conflicts ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                  {conflicts.has_conflicts ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 font-medium text-red-700">
                        <AlertTriangle className="w-4 h-4" />Conflicts Detected
                      </div>
                      {conflicts.conflicts.map((c, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs ${c.severity === 'HIGH' ? 'text-red-600' : 'text-amber-600'}`}>
                          <Badge variant={c.severity === 'HIGH' ? 'destructive' : 'secondary'} className="text-xs">{c.severity}</Badge>
                          <span className="font-medium">{c.type.replace(/_/g, ' ')}:</span>
                          <span>{c.message}</span>
                        </div>
                      ))}
                      {conflicts.conflicts.some(c => c.severity === 'HIGH') && (
                        <p className="text-xs text-red-500 mt-1">Cannot assign staff with HIGH severity conflicts</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-4 h-4" />No conflicts - Staff is available
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {shift.notes && (
            <div>
              <Label className="mb-1 block text-muted-foreground">Notes</Label>
              <p className="text-sm">{shift.notes}</p>
            </div>
          )}

          {/* Check-In / Check-Out Actions */}
          {shift.assigned_staff?.length > 0 && (
            <ShiftCheckInOut shift={shift} onUpdate={onUpdate} />
          )}

          {/* Phase 1.5 — operational service activity breakdown (admin-only, no billing impact) */}
          <ServiceActivityBreakdownPanel shift={shift} onChange={onUpdate} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════
// StatusLegend — Uses getShiftStatusMeta
// ════════════════════════════════════════════════════════════════

function StatusLegend() {
  const items = ['OPEN', 'PARTIALLY_FILLED', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'NO_SHOW', 'INVOICED'];
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground justify-center flex-wrap" data-testid="status-legend">
      {items.map(status => {
        const meta = getShiftStatusMeta(status);
        return (
          <span key={status} className="flex items-center gap-1">
            <span className={`w-2.5 h-2.5 rounded-full ${meta.dotColor}`} />
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// BulkActionBar — Shown when shifts are selected
// ════════════════════════════════════════════════════════════════

function BulkActionBar({ selectedCount, onClear, onBulkCancel, onBulkAssign, executing }) {
  if (selectedCount === 0) return null;
  return (
    <div className="sticky top-0 z-20 bg-blue-600 text-white rounded-lg px-4 py-2.5 flex items-center justify-between shadow-lg animate-fade-in" data-testid="bulk-action-bar">
      <div className="flex items-center gap-3">
        <Badge variant="secondary" className="bg-white/20 text-white border-0 text-sm">{selectedCount} selected</Badge>
        <Button variant="ghost" size="sm" className="text-white/80 hover:text-white hover:bg-white/10 h-7 text-xs" onClick={onClear} data-testid="bulk-clear-selection">
          <XCircle className="w-3.5 h-3.5 mr-1" />Clear
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={onBulkAssign} disabled={executing} data-testid="bulk-assign-btn">
          <UserPlus className="w-3.5 h-3.5 mr-1" />Assign Staff
        </Button>
        <Button size="sm" variant="secondary" className="h-7 text-xs bg-red-100 text-red-700 hover:bg-red-200 border-red-300" onClick={onBulkCancel} disabled={executing} data-testid="bulk-cancel-btn">
          <Trash2 className="w-3.5 h-3.5 mr-1" />Cancel Shifts
        </Button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MonthGrid — Month view calendar grid
// ════════════════════════════════════════════════════════════════

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_PREVIEWS = 3;

function MonthGrid({ currentDate, shifts, onShiftClick, onDayClick, onSlotClick }) {
  const { cells } = useMemo(() => buildMonthCells(currentDate), [currentDate]);
  const shiftsByDate = useMemo(() => groupShiftsByDate(shifts), [shifts]);

  return (
    <Card data-testid="month-grid">
      <CardContent className="p-0">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 border-b">
          {WEEKDAY_HEADERS.map(day => (
            <div key={day} className="text-center py-2 text-xs font-medium text-muted-foreground bg-muted/30 border-r last:border-r-0">
              {day}
            </div>
          ))}
        </div>

        {/* Day cells grid */}
        <div className="grid grid-cols-7">
          {cells.map(cell => {
            const dayShifts = shiftsByDate.get(cell.dateStr) || [];
            const summary = getDayStatusSummary(dayShifts);
            const previews = dayShifts.slice(0, MAX_PREVIEWS);
            const overflow = dayShifts.length - MAX_PREVIEWS;

            return (
              <div
                key={cell.dateStr}
                className={`border-r border-b last:border-r-0 min-h-[100px] p-1 transition-colors ${
                  cell.isCurrentMonth ? 'bg-background' : 'bg-muted/20'
                } ${cell.isToday ? 'bg-blue-50/80 ring-1 ring-inset ring-blue-200' : ''}`}
                data-testid={`month-cell-${cell.dateStr}`}
              >
                {/* Day header */}
                <div className="flex items-center justify-between mb-0.5">
                  <button
                    onClick={() => onDayClick(cell.date, 'week')}
                    className={`text-xs font-medium rounded-full w-6 h-6 flex items-center justify-center transition-colors hover:bg-primary hover:text-primary-foreground ${
                      cell.isToday ? 'bg-blue-600 text-white' : cell.isCurrentMonth ? 'text-foreground' : 'text-muted-foreground/50'
                    }`}
                    data-testid={`month-day-${cell.dateStr}`}
                  >
                    {format(cell.date, 'd')}
                  </button>
                  {dayShifts.length > 0 && (
                    <div className="flex gap-0.5">
                      {summary.open > 0 && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title={`${summary.open} open`} />}
                      {summary.partial > 0 && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title={`${summary.partial} partial`} />}
                      {summary.assigned > 0 && <span className="w-1.5 h-1.5 rounded-full bg-green-500" title={`${summary.assigned} assigned`} />}
                    </div>
                  )}
                </div>

                {/* Shift count */}
                {dayShifts.length > 0 && (
                  <div className="text-[10px] text-muted-foreground mb-0.5">{dayShifts.length} shift{dayShifts.length !== 1 ? 's' : ''}</div>
                )}

                {/* Shift previews */}
                <div className="space-y-0.5">
                  {previews.map(shift => {
                    const meta = shift._isGroupSession
                      ? { dotColor: 'bg-indigo-500' }
                      : getShiftStatusMeta(shift.status);
                    return (
                      <button
                        key={shift.id}
                        onClick={(e) => { e.stopPropagation(); onShiftClick(shift); }}
                        className="w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded hover:bg-muted truncate flex items-center gap-1"
                        data-testid={`month-shift-${shift.id}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meta.dotColor}`} />
                        <span className="truncate">{shift.start_time} {shift.participant_name || shift.title || ''}</span>
                      </button>
                    );
                  })}
                  {overflow > 0 && (
                    <button
                      onClick={() => onDayClick(cell.date, 'day')}
                      className="w-full text-[10px] text-primary hover:underline text-left px-1"
                      data-testid={`month-more-${cell.dateStr}`}
                    >
                      +{overflow} more
                    </button>
                  )}
                </div>

                {/* Empty slot click area */}
                {dayShifts.length === 0 && cell.isCurrentMonth && (
                  <button
                    onClick={() => onSlotClick(cell.dateStr)}
                    className="w-full h-12 flex items-center justify-center text-[10px] text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/50 rounded transition-colors"
                    data-testid={`month-empty-${cell.dateStr}`}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════
// SummaryCard — Clickable stat card
// ════════════════════════════════════════════════════════════════

function SummaryCard({ label, value, icon: Icon, color = '', accent = '', onClick }) {
  return (
    <Card className={`cursor-pointer hover:shadow-md transition-shadow ${accent}`} onClick={onClick} data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-2.5 flex items-center justify-between">
        <div>
          <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
          <p className={`text-lg font-bold ${color}`}>{value}</p>
        </div>
        <Icon className={`w-5 h-5 ${color || 'text-muted-foreground/40'}`} />
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════
// EmptyFilterState — Shown when filters produce no shifts
// ════════════════════════════════════════════════════════════════

function EmptyFilterState({ onClear }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Filter className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
        <p className="text-sm font-medium text-muted-foreground">No shifts match your filters</p>
        <p className="text-xs text-muted-foreground mt-1">Try adjusting or clearing your filters</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={onClear} data-testid="empty-clear-filters">
          <XCircle className="w-4 h-4 mr-1" />Clear All Filters
        </Button>
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════
// QuickCreateModal — Lightweight shift creation using existing API
// ════════════════════════════════════════════════════════════════

function QuickCreateModal({ open, onClose, onCreated, prefillDate, participants, services }) {
  const [form, setForm] = useState({
    shift_date: '', service_id: '', participant_id: '',
    start_time: '09:00', end_time: '17:00', required_staff: 1, notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form when opening with new prefill
  useEffect(() => {
    if (open) {
      setForm(prev => ({
        ...prev,
        shift_date: prefillDate || format(new Date(), 'yyyy-MM-dd'),
        service_id: '', participant_id: '',
        start_time: '09:00', end_time: '17:00', required_staff: 1, notes: '',
      }));
      setError('');
    }
  }, [open, prefillDate]);

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validate = () => {
    if (!form.shift_date) return 'Date is required';
    if (!form.start_time) return 'Start time is required';
    if (!form.end_time) return 'End time is required';
    if (!form.service_id) return 'Service is required';
    return '';
  };

  const handleSubmit = async () => {
    const validationErr = validate();
    if (validationErr) { setError(validationErr); return; }

    setSubmitting(true);
    setError('');
    try {
      await api.post('/roster/shifts/create', {
        shift_date: form.shift_date,
        service_id: form.service_id,
        start_time: form.start_time,
        end_time: form.end_time,
        required_staff: form.required_staff,
        participant_id: form.participant_id || undefined,
        notes: form.notes || undefined,
      });
      toast.success('Shift created successfully');
      onCreated();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data;
      const msg = typeof detail === 'string' ? detail
        : detail?.error?.message || detail?.message || 'Failed to create shift';
      setError(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="quick-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />Quick Create Shift
          </DialogTitle>
          <DialogDescription>
            Create a new shift for {form.shift_date ? format(parseISO(form.shift_date), 'EEEE, MMM d, yyyy') : 'selected date'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md p-3" data-testid="quick-create-error">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date *</Label>
              <input type="date" value={form.shift_date} onChange={e => updateForm('shift_date', e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="qc-date" />
            </div>
            <div>
              <Label className="text-xs">Required Staff</Label>
              <input type="number" min="1" max="20" value={form.required_staff}
                onChange={e => updateForm('required_staff', parseInt(e.target.value) || 1)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="qc-required-staff" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Start Time *</Label>
              <input type="time" value={form.start_time} onChange={e => updateForm('start_time', e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="qc-start-time" />
            </div>
            <div>
              <Label className="text-xs">End Time *</Label>
              <input type="time" value={form.end_time} onChange={e => updateForm('end_time', e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="qc-end-time" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Service *</Label>
            <Select value={form.service_id || '_none'} onValueChange={v => updateForm('service_id', v === '_none' ? '' : v)}>
              <SelectTrigger data-testid="qc-service"><SelectValue placeholder="Select service" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">-- Select Service --</SelectItem>
                {services.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Participant (optional)</Label>
            <Select value={form.participant_id || '_none'} onValueChange={v => updateForm('participant_id', v === '_none' ? '' : v)}>
              <SelectTrigger data-testid="qc-participant"><SelectValue placeholder="No participant" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">-- No Participant --</SelectItem>
                {participants.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <input type="text" value={form.notes} onChange={e => updateForm('notes', e.target.value)}
              placeholder="Shift notes..."
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="qc-notes" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="qc-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="qc-submit">
            {submitting ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Creating...</> : <><Plus className="w-4 h-4 mr-1" />Create Shift</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ════════════════════════════════════════════════════════════════
// TimeGutter — Day 7: Visible hour labels for Week/Day views
// Purely visual; does not position shifts by time.
// ════════════════════════════════════════════════════════════════

const HOUR_MARKERS = ['06:00', '09:00', '12:00', '15:00', '18:00', '21:00'];

function TimeGutter() {
  return (
    <div className="w-12 shrink-0 border-r bg-muted/30 select-none" data-testid="time-gutter">
      <div className="text-center py-2 border-b text-[10px] font-medium text-muted-foreground tracking-wide uppercase">
        Time
      </div>
      <div
        className="px-1 py-1 min-h-[200px] text-[10px] text-muted-foreground"
        style={{
          backgroundImage: 'repeating-linear-gradient(to bottom, transparent 0px, transparent 39px, rgba(100, 116, 139, 0.15) 39px, rgba(100, 116, 139, 0.15) 40px)',
          backgroundSize: '100% 40px',
        }}
      >
        {HOUR_MARKERS.map((label, i) => (
          <div
            key={label}
            className="tabular-nums text-right pr-0.5 leading-none"
            style={{ height: '40px', paddingTop: i === 0 ? '2px' : '6px' }}
            data-testid={`time-marker-${label}`}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// CalendarSkeletons — Day 7: Loading skeletons per view
// ════════════════════════════════════════════════════════════════

function SummaryCardsSkeleton() {
  return (
    <>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="summary-skeleton-row1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-2.5 flex items-center justify-between">
              <div className="space-y-1.5">
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="h-5 w-10" />
              </div>
              <Skeleton className="h-5 w-5 rounded-full" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="summary-skeleton-row2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-2.5 flex items-center justify-between">
              <div className="space-y-1.5">
                <Skeleton className="h-2.5 w-14" />
                <Skeleton className="h-5 w-8" />
              </div>
              <Skeleton className="h-5 w-5 rounded-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}

function ShiftCardSkeleton() {
  return (
    <div className="border rounded-md p-2 bg-muted/30 space-y-1.5" data-testid="shift-skeleton">
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="h-2.5 w-16" />
      <Skeleton className="h-2.5 w-20" />
    </div>
  );
}

function WeekViewSkeleton() {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return (
    <Card data-testid="week-view-skeleton">
      <CardContent className="p-0">
        <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b">
          <Skeleton className="h-3 w-3" />
          <Skeleton className="h-3 w-48" />
        </div>
        <div className="flex border-b">
          <div className="w-12 shrink-0 border-r bg-muted/30">
            <div className="text-center py-2 border-b">
              <Skeleton className="h-2 w-8 mx-auto" />
            </div>
            <div className="p-1 space-y-5 min-h-[200px]">
              {HOUR_MARKERS.map((_, i) => <Skeleton key={i} className="h-2 w-8" />)}
            </div>
          </div>
          {days.map(d => (
            <div key={d} className="flex-1 min-w-[140px] border-r last:border-r-0">
              <div className="text-center py-2 border-b space-y-1">
                <Skeleton className="h-2 w-6 mx-auto" />
                <Skeleton className="h-4 w-4 mx-auto" />
              </div>
              <div className="p-1 space-y-1 min-h-[200px]">
                <ShiftCardSkeleton />
                {Math.random() > 0.4 && <ShiftCardSkeleton />}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function DayViewSkeleton() {
  return (
    <Card data-testid="day-view-skeleton">
      <CardContent className="p-4 space-y-3">
        <Skeleton className="h-5 w-56 mx-auto" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/30">
              <div className="flex items-center justify-between">
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-4 w-16 rounded-full" />
              </div>
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-3 w-36" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function MonthViewSkeleton() {
  return (
    <Card data-testid="month-view-skeleton">
      <CardContent className="p-0">
        <div className="grid grid-cols-7 border-b">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <div key={d} className="text-center py-2 bg-muted/30 border-r last:border-r-0">
              <Skeleton className="h-2.5 w-8 mx-auto" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="border-r border-b last:border-r-0 min-h-[100px] p-1 space-y-1 bg-muted/10">
              <Skeleton className="h-5 w-5 rounded-full" />
              {Math.random() > 0.5 && <Skeleton className="h-2.5 w-full" />}
              {Math.random() > 0.7 && <Skeleton className="h-2.5 w-4/5" />}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function CalendarViewSkeleton({ viewMode }) {
  if (viewMode === 'month') return <MonthViewSkeleton />;
  if (viewMode === 'day') return <DayViewSkeleton />;
  return <WeekViewSkeleton />;
}

// ════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT — Data-fetch + interaction logic UNCHANGED
// ════════════════════════════════════════════════════════════════

export default function RosterCalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id || user?.email || null;
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [groupBy, setGroupBy] = useState('none');
  // Day 11 — Operational charts (insights) visibility. Default ON for desktop-sized windows;
  // persisted in localStorage so admin users' preference sticks across sessions.
  const [showInsights, setShowInsights] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('calendar.showInsights.v1') : null;
      if (raw === null) return true;
      return raw === '1';
    } catch { return true; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('calendar.showInsights.v1', showInsights ? '1' : '0'); } catch { /* ignore */ }
  }, [showInsights]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [staff, setStaff] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('');
  const [participantFilter, setParticipantFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [quickCreateDate, setQuickCreateDate] = useState('');
  const [participants, setParticipants] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedShiftIds, setSelectedShiftIds] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(null); // { action, staffId? }
  const [bulkExecuting, setBulkExecuting] = useState(false);
  const [bulkAssignStaff, setBulkAssignStaff] = useState('');
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false);
  const [activeSavedViewId, setActiveSavedViewId] = useState(null);
  const [defaultApplied, setDefaultApplied] = useState(false);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  // Month grid cells (memoized) — used by month view
  const monthData = useMemo(() => buildMonthCells(currentDate), [currentDate]);

  // Compute fetch range based on viewMode
  const fetchRange = useMemo(() => {
    if (viewMode === 'month') {
      return {
        start: format(monthData.gridStart, 'yyyy-MM-dd'),
        end: format(monthData.gridEnd, 'yyyy-MM-dd'),
      };
    }
    return {
      start: format(weekStart, 'yyyy-MM-dd'),
      end: format(weekEnd, 'yyyy-MM-dd'),
    };
  }, [viewMode, weekStart, weekEnd, monthData.gridStart, monthData.gridEnd]);

  // ── Data fetch: uses computed date range ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [shiftsRes, staffRes, gsRes, servicesRes, participantsRes] = await Promise.all([
        api.get(`/roster/shifts?start=${fetchRange.start}&end=${fetchRange.end}`),
        // /admin/users is admin-scoped; coordinators / roster managers may get 403.
        // Fail gracefully — staff picker stays empty, the rest of the calendar still renders.
        api.get('/admin/users').catch(() => ({ data: { data: [] } })),
        api.get(`/group-sessions/calendar/events?start=${fetchRange.start}&end=${fetchRange.end}`).catch(() => ({ data: { data: [] } })),
        api.get('/services/catalog').catch(() => ({ data: [] })),
        api.get('/participants?limit=200').catch(() => ({ data: { data: [] } })),
      ]);

      const enrichedShifts = shiftsRes.data.items || shiftsRes.data.data || shiftsRes.data || [];

      const gsEvents = (gsRes.data.data || []).map(gs => ({
        ...gs,
        id: `gs-${gs.id}`,
        _gs_id: gs.id,
        title: gs.title,
        date: gs.date,
        start_time: gs.start_time || '',
        end_time: gs.end_time || '',
        status: gs.status === 'published' ? 'CONFIRMED' : gs.status === 'in_progress' ? 'IN_PROGRESS' : gs.status === 'completed' ? 'COMPLETED' : 'OPEN',
        _isGroupSession: true,
        participant_name: `${gs.participant_count} participants`,
        required_staff: gs.required_staff_count,
        assigned_staff: Array.from({ length: gs.assigned_staff_count }, (_, i) => ({ name: `Staff ${i + 1}` })),
      }));

      setShifts([...enrichedShifts, ...gsEvents]);
      setStaff((staffRes.data.data || []).filter(s => s.role_name === 'Support Worker' || s.role_name === 'Staff'));

      // Services from catalog (same source as roster create pages)
      const svcData = servicesRes.data?.data || servicesRes.data || [];
      setServices((Array.isArray(svcData) ? svcData : []).map(s => ({
        id: s.id || s._id, name: s.name || s.service_name || s.title || 'Service',
      })));

      // Participants (scoped by backend)
      const ptData = participantsRes.data?.data || participantsRes.data?.items || [];
      setParticipants((Array.isArray(ptData) ? ptData : []).map(p => ({
        id: p.id || p._id, name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Participant',
      })));
    } catch (e) {
      console.error('Failed to load shifts:', e);
      toast.error('Failed to load shifts');
    } finally { setLoading(false); }
  }, [fetchRange.start, fetchRange.end]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filter options: extracted from loaded data (memoized) ──
  const filterOptions = useMemo(() => extractFilterOptions(shifts), [shifts]);

  // ── Filter logic: uses calendarMetrics helper with AND stacking ──
  const filteredShifts = useMemo(() =>
    applyClientFilters(shifts, { statusFilter, staffFilter, participantFilter, serviceFilter }),
    [shifts, statusFilter, staffFilter, participantFilter, serviceFilter]
  );

  // ── Summary metrics: derived from ALL loaded shifts (unfiltered) ──
  const metrics = useMemo(() => getCalendarSummaryMetrics(shifts), [shifts]);

  // ── Active filter count for badge ──
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (statusFilter && statusFilter !== 'all') count++;
    if (staffFilter) count++;
    if (participantFilter) count++;
    if (serviceFilter) count++;
    return count;
  }, [statusFilter, staffFilter, participantFilter, serviceFilter]);

  const clearAllFilters = () => {
    setStatusFilter('all');
    setStaffFilter('');
    setParticipantFilter('');
    setServiceFilter('');
  };

  // ── Navigation: view-mode-aware ──
  const goToToday = () => setCurrentDate(new Date());
  const goPrev = () => setCurrentDate(prev => viewMode === 'month' ? subMonths(prev, 1) : subWeeks(prev, 1));
  const goNext = () => setCurrentDate(prev => viewMode === 'month' ? addMonths(prev, 1) : addWeeks(prev, 1));

  // ── Keyboard shortcuts (Day 8 add-on) ──
  // Shortcuts: W=Week, D=Day, M=Month, T=Today, ←/→ navigate, / open filter panel, ?=help
  // Guarded against firing while typing in inputs, textareas, selects, or any open dialog.
  useEffect(() => {
    const handler = (e) => {
      // Ignore if modifier pressed (don't hijack browser shortcuts)
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Ignore if focus is in an editable element or a dialog is open
      const target = e.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (target?.isContentEditable) return;
      // Ignore if any Radix dialog / alert-dialog is open (quick-create, bulk, shift detail)
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      if (document.querySelector('[role="alertdialog"][data-state="open"]')) return;

      const key = e.key;
      if (key === 'w' || key === 'W') { e.preventDefault(); setViewMode('week'); }
      else if (key === 'd' || key === 'D') { e.preventDefault(); setViewMode('day'); }
      else if (key === 'm' || key === 'M') { e.preventDefault(); setViewMode('month'); }
      else if (key === 't' || key === 'T') { e.preventDefault(); goToToday(); }
      else if (key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (key === '/') {
        e.preventDefault();
        // Toggle the filter panel via its existing toggle button
        const btn = document.querySelector('[data-testid="filter-toggle-btn"]');
        if (btn) btn.click();
      }
      else if (key === '?') {
        e.preventDefault();
        setKeyboardHelpOpen(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [viewMode]);

  // ── Selection: clear on view/date change ──
  useEffect(() => { setSelectedShiftIds(new Set()); }, [viewMode, fetchRange.start]);

  const toggleShiftSelection = useCallback((shiftId) => {
    setSelectedShiftIds(prev => {
      const next = new Set(prev);
      if (next.has(shiftId)) next.delete(shiftId);
      else next.add(shiftId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedShiftIds(new Set()), []);

  // ── Saved Views (Day 9): collect current UI state + applier ──
  const currentSavedViewState = useMemo(() => sanitizeState({
    viewMode, groupBy,
    statusFilter, staffFilter, participantFilter, serviceFilter,
    dateAnchor: format(currentDate, 'yyyy-MM-dd'),
  }), [viewMode, groupBy, statusFilter, staffFilter, participantFilter, serviceFilter, currentDate]);

  const applySavedView = useCallback((view) => {
    if (!view || !view.state) return;
    const s = view.state;
    // Apply only known-safe keys. Unknown fields are ignored.
    if (s.viewMode === 'week' || s.viewMode === 'day' || s.viewMode === 'month') setViewMode(s.viewMode);
    if (typeof s.groupBy === 'string') setGroupBy(s.groupBy);
    if (typeof s.statusFilter === 'string') setStatusFilter(s.statusFilter);
    setStaffFilter(typeof s.staffFilter === 'string' ? s.staffFilter : '');
    setParticipantFilter(typeof s.participantFilter === 'string' ? s.participantFilter : '');
    setServiceFilter(typeof s.serviceFilter === 'string' ? s.serviceFilter : '');
    if (typeof s.dateAnchor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.dateAnchor)) {
      try { setCurrentDate(parseISO(s.dateAnchor)); } catch { /* ignore bad date */ }
    }
    // Clear transient selection state to avoid mismatched IDs after filter change
    setSelectedShiftIds(new Set());
    setActiveSavedViewId(view.id);
  }, []);

  // Apply default saved view on first load (once user context is available)
  useEffect(() => {
    if (defaultApplied) return;
    if (!user) return;
    const def = getDefaultView(userId);
    if (def) applySavedView(def);
    setDefaultApplied(true);
  }, [user, userId, defaultApplied, applySavedView]);

  // If the user manually diverges from the active saved view, drop the "active" indicator.
  // Compared cheaply on the already-memoized sanitized state.
  useEffect(() => {
    if (!activeSavedViewId) return;
    // Re-read the saved views store to compare
    const store = typeof window !== 'undefined' ? window.localStorage : null;
    if (!store) return;
    // Soft-diff via JSON of the selector state vs what was applied.
    const active = (() => {
      try {
        const raw = store.getItem(`getcare360:calendar:savedViews:v1:${userId || 'anon'}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.views?.find(v => v.id === activeSavedViewId) || null;
      } catch { return null; }
    })();
    if (!active) { setActiveSavedViewId(null); return; }
    const same = JSON.stringify(sanitizeState(active.state)) === JSON.stringify(currentSavedViewState);
    if (!same) setActiveSavedViewId(null);
  }, [currentSavedViewState, activeSavedViewId, userId]);

  // ── Bulk Cancel: sequential DELETE per shift ──
  const executeBulkCancel = async () => {
    setBulkConfirm(null);
    setBulkExecuting(true);
    let success = 0, failed = 0;
    const ids = [...selectedShiftIds];
    for (const id of ids) {
      try {
        await api.delete(`/roster/shifts/${id}`);
        success++;
      } catch (e) {
        failed++;
      }
    }
    setBulkExecuting(false);
    clearSelection();
    fetchData();
    if (failed === 0) {
      toast.success(`${success} shift${success !== 1 ? 's' : ''} cancelled`);
    } else {
      toast.error(`${success} cancelled, ${failed} failed`);
    }
  };

  // ── Bulk Assign: uses existing bulk-assign endpoint ──
  const executeBulkAssign = async () => {
    const staffId = bulkConfirm?.staffId;
    setBulkConfirm(null);
    if (!staffId) { toast.error('No staff selected'); return; }
    setBulkExecuting(true);
    try {
      const assignments = [...selectedShiftIds].map(sid => ({ shift_id: sid, staff_id: staffId }));
      const res = await api.post('/roster/shifts/bulk-assign', { assignments });
      const r = res.data?.data || {};
      clearSelection();
      fetchData();
      if (r.failed === 0 || (r.assigned === r.total)) {
        toast.success(`${r.assigned} shift${r.assigned !== 1 ? 's' : ''} assigned`);
      } else {
        toast.error(`${r.assigned} assigned, ${r.total - r.assigned} failed/skipped`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk assign failed');
    }
    setBulkExecuting(false);
    setBulkAssignStaff('');
  };

  // ── Interaction handlers: UNCHANGED ──
  const handleShiftClick = (shift) => {
    if (shift._isGroupSession) {
      navigate(`/admin/group-sessions/${shift._gs_id}`);
      return;
    }
    setSelectedShift(shift);
  };

  const handleUpdate = () => {
    fetchData();
    setSelectedShift(null);
  };

  const handleCreateShift = () => {
    setQuickCreateDate(format(currentDate, 'yyyy-MM-dd'));
    setQuickCreateOpen(true);
  };

  const handleSlotClick = (dateStr) => {
    setQuickCreateDate(dateStr);
    setQuickCreateOpen(true);
  };

  const handleQuickCreateDone = () => {
    fetchData();
  };

  const handleMonthDayClick = (date, targetView) => {
    setCurrentDate(date);
    setViewMode(targetView);
  };

  // ── Drag and drop: UNCHANGED ──
  const handleDragEnd = async (result) => {
    const { draggableId, source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newDate = destination.droppableId.replace('day-', '');
    const shift = shifts.find(s => s.id === draggableId);
    if (!shift) return;
    if (shift.date === newDate) return;

    const oldDate = shift.date;
    setShifts(prev => prev.map(s => s.id === draggableId ? { ...s, date: newDate } : s));

    try {
      await api.put(`/roster/shifts/${draggableId}`, { shift_date: newDate });
      toast.success(`Shift moved to ${format(parseISO(newDate), 'EEE, MMM d')}`);
    } catch (e) {
      setShifts(prev => prev.map(s => s.id === draggableId ? { ...s, date: oldDate } : s));
      toast.error(e.response?.data?.detail || 'Failed to reschedule shift');
    }
  };

  return (
    <TooltipProvider delayDuration={300} skipDelayDuration={150}>
    <div className="space-y-4 animate-fade-in" data-testid="roster-calendar-page">
      {/* Toolbar */}
      <CalendarToolbar
        currentDate={currentDate}
        weekStart={weekStart}
        weekEnd={weekEnd}
        viewMode={viewMode}
        setViewMode={setViewMode}
        groupBy={groupBy}
        setGroupBy={setGroupBy}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        staffFilter={staffFilter}
        setStaffFilter={setStaffFilter}
        participantFilter={participantFilter}
        setParticipantFilter={setParticipantFilter}
        serviceFilter={serviceFilter}
        setServiceFilter={setServiceFilter}
        filterOptions={filterOptions}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearAllFilters}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToToday}
        onRefresh={fetchData}
        onCreateShift={handleCreateShift}
        onOpenKeyboardHelp={() => setKeyboardHelpOpen(true)}
        savedViewsSlot={
          <SavedViewsMenu
            userId={userId}
            currentState={currentSavedViewState}
            activeViewId={activeSavedViewId}
            onApply={applySavedView}
          />
        }
      />

      {/* Summary Cards — derived from ALL loaded shifts (unfiltered) */}
      {loading ? (
        <SummaryCardsSkeleton />
      ) : (
        <>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2" data-testid="calendar-summary-slot">
        <SummaryCard label="Total" value={metrics.total} icon={CalendarIcon} onClick={() => setStatusFilter('all')} />
        <SummaryCard label="Open" value={metrics.open} icon={AlertTriangle}
          color={metrics.open > 0 ? 'text-red-600' : ''} accent={metrics.open > 0 ? 'border-red-200 bg-red-50/50' : ''}
          onClick={() => setStatusFilter('OPEN')} />
        <SummaryCard label="Partial" value={metrics.partial} icon={Users}
          color={metrics.partial > 0 ? 'text-amber-600' : ''} accent={metrics.partial > 0 ? 'border-amber-200 bg-amber-50/50' : ''}
          onClick={() => setStatusFilter('PARTIALLY_FILLED')} />
        <SummaryCard label="Assigned" value={metrics.assigned} icon={CheckCircle} color="text-green-600"
          onClick={() => setStatusFilter('assigned')} />
        <SummaryCard label="Coverage" value={`${metrics.coveragePercent}%`} icon={BarChart3} color="text-blue-600" />
      </div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        <SummaryCard label="In Progress" value={metrics.inProgress} icon={Clock} color="text-purple-600"
          onClick={() => setStatusFilter('IN_PROGRESS')} />
        <SummaryCard label="Completed" value={metrics.completed} icon={CheckCircle} color="text-gray-500"
          onClick={() => setStatusFilter('COMPLETED')} />
        <SummaryCard label="No Show" value={metrics.noShow} icon={XCircle}
          color={metrics.noShow > 0 ? 'text-rose-600' : ''} accent={metrics.noShow > 0 ? 'border-rose-200 bg-rose-50/50' : ''}
          onClick={() => setStatusFilter('NO_SHOW')} />
        <SummaryCard label="Invoiced" value={metrics.invoiced} icon={CalendarIcon} color="text-sky-600"
          onClick={() => setStatusFilter('INVOICED')} />
        <SummaryCard label="Sched. Hours" value={metrics.totalScheduledHours} icon={Timer} color="text-indigo-600" />
      </div>
        </>
      )}

      {/* Day 11 — Operational charts toggle + panel */}
      {!loading && (
        <div className="flex items-center justify-between" data-testid="insights-toggle-row">
          <span className="text-[11px] text-muted-foreground">
            {showInsights
              ? `Insights show ${filteredShifts.length} shift${filteredShifts.length === 1 ? '' : 's'} in current view`
              : 'Operational insights hidden'}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setShowInsights(v => !v)}
            data-testid="toggle-insights-btn"
          >
            <PieIcon className="w-3.5 h-3.5 mr-1" />
            {showInsights ? 'Hide insights' : 'Show insights'}
          </Button>
        </div>
      )}
      {showInsights && !loading && (
        <CalendarOperationalCharts shifts={filteredShifts} />
      )}

      {/* Active filter indicators */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 text-xs" data-testid="active-filter-bar">
          <span className="text-muted-foreground">Showing {filteredShifts.length} of {shifts.length} shifts</span>
          <span className="text-muted-foreground">|</span>
          {statusFilter !== 'all' && <Badge variant="secondary" className="text-xs">Status: {statusFilter}</Badge>}
          {staffFilter && <Badge variant="secondary" className="text-xs">Staff: {filterOptions.staffOptions.find(s => s.id === staffFilter)?.name || '...'}</Badge>}
          {participantFilter && <Badge variant="secondary" className="text-xs">Participant: {filterOptions.participantOptions.find(p => p.id === participantFilter)?.name || '...'}</Badge>}
          {serviceFilter && <Badge variant="secondary" className="text-xs">Service: {filterOptions.serviceOptions.find(s => s.id === serviceFilter)?.name || '...'}</Badge>}
          <Button variant="ghost" size="sm" className="h-5 px-1 text-xs text-muted-foreground" onClick={clearAllFilters}>
            <XCircle className="w-3 h-3 mr-0.5" />Clear
          </Button>
        </div>
      )}

      {/* Calendar Content */}
      {loading ? (
        <CalendarViewSkeleton viewMode={viewMode} />
      ) : filteredShifts.length === 0 && activeFilterCount > 0 ? (
        <EmptyFilterState onClear={clearAllFilters} />
      ) : viewMode === 'month' ? (
        <>
          {groupBy !== 'none' && (
            <div className="text-xs text-muted-foreground bg-muted/30 rounded px-3 py-2 flex items-center gap-2">
              <CalendarDays className="w-3.5 h-3.5" />
              Grouping is not available in month view. Switch to Week or Day view for grouped display.
            </div>
          )}
          <MonthGrid
            currentDate={currentDate}
            shifts={filteredShifts}
            onShiftClick={handleShiftClick}
            onDayClick={handleMonthDayClick}
            onSlotClick={handleSlotClick}
          />
        </>
      ) : groupBy !== 'none' ? (
        <GroupedView shifts={filteredShifts} groupBy={groupBy} onShiftClick={handleShiftClick} />
      ) : viewMode === 'week' ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 border-b text-xs text-muted-foreground">
                <Move className="w-3 h-3" />
                <span>Drag shifts to reschedule • Dashed cards are group sessions (managed separately)</span>
              </div>
              <div className="flex border-b">
                <TimeGutter />
                {weekDays.map(day => (
                  <DayColumn
                    key={day.toISOString()}
                    date={day}
                    shifts={filteredShifts}
                    onShiftClick={handleShiftClick}
                    onSlotClick={handleSlotClick}
                    isToday={isSameDay(day, new Date())}
                    droppableId={`day-${format(day, 'yyyy-MM-dd')}`}
                    selectedShiftIds={selectedShiftIds}
                    onToggleSelect={toggleShiftSelection}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </DragDropContext>
      ) : (
        <Card>
          <CardContent className="p-4">
            <div className="text-center mb-4">
              <h2 className="text-lg font-semibold">{format(currentDate, 'EEEE, MMMM d, yyyy')}</h2>
            </div>
            <div className="space-y-2">
              {filteredShifts
                .filter(s => s.date === format(currentDate, 'yyyy-MM-dd'))
                .sort((a, b) => a.start_time.localeCompare(b.start_time))
                .map(shift => (
                  <ShiftCard key={shift.id} shift={shift} onClick={handleShiftClick}
                    selectable={!shift._isGroupSession} selected={selectedShiftIds.has(shift.id)} onToggleSelect={toggleShiftSelection} />
                ))}
              {filteredShifts.filter(s => s.date === format(currentDate, 'yyyy-MM-dd')).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">No shifts for this day</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Action Bar */}
      {viewMode !== 'month' && (
        <BulkActionBar
          selectedCount={selectedShiftIds.size}
          onClear={clearSelection}
          onBulkCancel={() => {
            const cancelableIds = [...selectedShiftIds].filter(id => {
              const s = shifts.find(sh => sh.id === id);
              return s && !['CANCELLED', 'IN_PROGRESS', 'COMPLETED', 'INVOICED'].includes(s.status);
            });
            if (cancelableIds.length === 0) { toast.error('No selected shifts can be cancelled'); return; }
            setSelectedShiftIds(new Set(cancelableIds));
            setBulkConfirm({ action: 'cancel' });
          }}
          onBulkAssign={() => {
            setBulkAssignStaff('');
            setBulkConfirm({ action: 'assign_select' });
          }}
          executing={bulkExecuting}
        />
      )}

      {/* Legend */}
      <StatusLegend />

      {/* Shift Detail Dialog */}
      <ShiftDetailDialog
        shift={selectedShift}
        open={!!selectedShift}
        onClose={() => setSelectedShift(null)}
        onUpdate={handleUpdate}
        staff={staff}
      />

      {/* Quick Create Modal */}
      <QuickCreateModal
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onCreated={handleQuickCreateDone}
        prefillDate={quickCreateDate}
        participants={participants}
        services={services}
      />

      {/* Bulk Cancel Confirmation */}
      <AlertDialog open={bulkConfirm?.action === 'cancel'} onOpenChange={v => !v && setBulkConfirm(null)}>
        <AlertDialogContent data-testid="bulk-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />Cancel {selectedShiftIds.size} Shift{selectedShiftIds.size !== 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>You are about to cancel <strong>{selectedShiftIds.size}</strong> selected shift{selectedShiftIds.size !== 1 ? 's' : ''}.</p>
                <p>Each shift will be cancelled individually through existing backend validation. Shifts that are already in progress, completed, or invoiced cannot be cancelled.</p>
                <p className="text-amber-600 font-medium">Some shifts may fail if they do not meet cancellation rules. Results will be reported.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-cancel-dismiss">Back</AlertDialogCancel>
            <AlertDialogAction onClick={executeBulkCancel} className="bg-red-600 hover:bg-red-700" data-testid="bulk-cancel-confirm-btn">
              Cancel {selectedShiftIds.size} Shift{selectedShiftIds.size !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Assign — Staff Selection Dialog */}
      <Dialog open={bulkConfirm?.action === 'assign_select'} onOpenChange={v => !v && setBulkConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />Assign Staff to {selectedShiftIds.size} Shift{selectedShiftIds.size !== 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>Select a staff member to assign to all selected shifts. Backend validation (conflicts, capacity, RBAC) still applies per shift.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs mb-1 block">Staff Member</Label>
            <Select value={bulkAssignStaff || '_none'} onValueChange={v => setBulkAssignStaff(v === '_none' ? '' : v)}>
              <SelectTrigger data-testid="bulk-assign-staff-select"><SelectValue placeholder="Select staff" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">-- Select Staff --</SelectItem>
                {staff.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirm(null)}>Cancel</Button>
            <Button
              onClick={() => { setBulkConfirm({ action: 'assign', staffId: bulkAssignStaff }); }}
              disabled={!bulkAssignStaff}
              data-testid="bulk-assign-proceed"
            >
              <UserPlus className="w-4 h-4 mr-1" />Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Confirmation */}
      <AlertDialog open={bulkConfirm?.action === 'assign'} onOpenChange={v => !v && setBulkConfirm(null)}>
        <AlertDialogContent data-testid="bulk-assign-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Assign <strong>{staff.find(s => s.id === bulkConfirm?.staffId)?.full_name || 'selected staff'}</strong> to <strong>{selectedShiftIds.size}</strong> shift{selectedShiftIds.size !== 1 ? 's' : ''}. Backend validation (conflicts, availability, RBAC) still applies. Some assignments may fail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction onClick={executeBulkAssign} data-testid="bulk-assign-confirm-btn">
              Assign to {selectedShiftIds.size} Shifts
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Keyboard Shortcuts Help Dialog */}
      <Dialog open={keyboardHelpOpen} onOpenChange={setKeyboardHelpOpen}>
        <DialogContent className="max-w-sm" data-testid="keyboard-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="w-5 h-5" />Keyboard Shortcuts
            </DialogTitle>
            <DialogDescription>Navigate the calendar faster with these keys.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {[
              { keys: ['W'], label: 'Week view' },
              { keys: ['D'], label: 'Day view' },
              { keys: ['M'], label: 'Month view' },
              { keys: ['T'], label: 'Jump to today' },
              { keys: ['←'], label: 'Previous week / month' },
              { keys: ['→'], label: 'Next week / month' },
              { keys: ['/'], label: 'Toggle filter panel' },
              { keys: ['?'], label: 'Toggle this help' },
            ].map(row => (
              <div key={row.label} className="flex items-center justify-between py-1.5 border-b last:border-0">
                <span className="text-muted-foreground">{row.label}</span>
                <div className="flex items-center gap-1">
                  {row.keys.map(k => (
                    <kbd key={k} className="px-2 py-0.5 text-xs font-mono bg-muted border rounded shadow-sm min-w-[28px] text-center" data-testid={`kbd-${k}`}>
                      {k}
                    </kbd>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground pt-2 italic">
              Shortcuts are disabled while typing in inputs or when a dialog is open.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKeyboardHelpOpen(false)} data-testid="kbd-help-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
